import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react'

import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { isAssistantUrlMalformed, isFromAssistant } from 'sidecar-utilities'

import {
  awaitNewTicket,
  fetchMemberCommsMessages,
  fetchUnresolvedMessages,
  INBOX_INDEXING_DELAY_MS,
} from 'store/MemberCommunication'
import {
  addChatAttributes,
  subscribeToMessengerState,
  openChatWithRecovery,
} from 'utils/chat/useChatWidget'
import { logError } from 'utils/monitoring/datadog'
import { isPhoneViewport } from 'utils/isPhoneViewport'
import { withAshSession } from 'utils/heap/withAshSession'

/**
 * The Sidekick context is used to provide the virtual assistant with
 * information about the current context of the member in the app.
 */
export const Sidekick = createContext()

/**
 * The SidekickProvider is a component that provides the virtual assistant with
 * information about the current context of the member in the app.
 *
 * @param {React.ReactNode} children - The children of the SidekickProvider
 *   component.
 * @returns {React.ReactNode} The open state, chat state, & Sidekick iframe URL
 */
export const SidekickProvider = ({ children }) => {
  const [isChatReady, setIsChatReady] = useState(false)
  const [isGenesysChatOpen, setIsGenesysChatOpen] = useState(false)
  // How the poptart should draw its header while Ash is showing something other than
  // the chat (`SET_PANEL_HEADER`): `{ title, canGoBack }`.
  //
  // Null means "your own header" — this provider deliberately does not know the
  // default title, which lives in `SidekickPoptart` where the rest of that header's
  // props do.
  //
  // One object rather than two states because it's one message and they always change
  // together; a header half-way between two of Ash's screens isn't a state that exists.
  //
  // State, not a ref, unlike `ashSessionIdRef`: the whole point is to re-render the
  // poptart with a different header, so a re-render is the feature.
  const [panelHeader, setPanelHeader] = useState(null)
  const listenerRef = useRef(null)
  // For `OPEN_MEMBER_INBOX` below. This provider is mounted inside `<Router>`
  // (`index.jsx`), so the hook is available here — and a client-side navigate is what
  // keeps the transition from reloading the SPA the member is already inside.
  //
  // Held in a ref because the `message` listener is registered exactly once (see the
  // `listenerRef` guard in the effect below), so anything it closes over is frozen at
  // first render. `navigate`'s identity is not guaranteed stable across renders, and a
  // frozen one is the kind of thing that works until the day it doesn't.
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  // Also for `OPEN_MEMBER_INBOX`: a client-side navigate reuses the store, and every
  // fetch on the way into `/inbox` is guarded by "only if we don't have it yet"
  // (`RequestList`, `Inbox/Home`) — so a member who opened Messages earlier this
  // session lands on the list they saw *before* filing, without their new ticket.
  // Dispatching the fetches ourselves is what makes the new one show up. See the
  // handler for why this is the provider's job and not the inbox's.
  //
  // Ref'd for the same reason as `navigate`: the listener below closes over first
  // render. `dispatch` is stable in practice, but nothing here should depend on that.
  const dispatch = useDispatch()
  const dispatchRef = useRef(dispatch)
  dispatchRef.current = dispatch

  // The live Ash session, sent out by the iframe (ASH_SESSION_STARTED) once the
  // member sends their first message. Empty until then — `withAshSession` owns
  // what that means for the events that read it.
  //
  // A ref, not state: its only readers are event handlers, so re-rendering every
  // consumer of this context on an analytics id would be churn for nothing.
  const ashSessionIdRef = useRef('')
  const getAshSessionId = useCallback(() => ashSessionIdRef.current, [])

  // This state and handlers are for managing the Sidekick poptart.
  const [isOpen, setIsOpen] = useState(false)

  // Whether the panel is drawn as the full screen rather than the docked,
  // resizable sheet. Below `xs` (480px) the sheet's height is computed, and every
  // term in that sum can come out short — which is how Ash reached production
  // opening as a stub near the bottom of the screen. Maximized has no
  // height arithmetic at all, so a phone gets that instead.
  //
  // Set in `openSidekick` below, and owned out here rather than in the poptart
  // for two reasons. The panel outlives its own close — `SidekickPoptart` stays
  // mounted and Radix only reports the closes it handles itself — so a choice
  // stored in there would leak into the next open. And re-deriving it from the
  // viewport on every render would yank an open panel between layouts the moment
  // the viewport crossed 480px, discarding a height the member had dragged.
  const [isMaximized, setIsMaximized] = useState(false)

  // A fresh open reads the viewport, so neither answer outlives its panel. The
  // panel can also already be up — it's `modal={false}`, so the help icon stays
  // tappable over a minimized sheet — and then a phone re-maximizes
  // but desktop is left alone, rather than force-minimized out of its own choice.
  const openSidekick = useCallback(() => {
    if (!isOpen) setIsMaximized(isPhoneViewport())
    else if (isPhoneViewport()) setIsMaximized(true)
    setIsOpen(true)
  }, [isOpen])

  // Every path that takes the panel down goes through here, so state that must
  // not survive into the next open has exactly one place to be cleared.
  //
  // `isMaximized` is deliberately not among it: `openSidekick` overwrites it, so
  // there is nothing to leak, and clearing it here would collapse the panel
  // under the member's eyes on the way out.
  const hideSidekick = useCallback(() => {
    setIsOpen(false)
    // `TicketComposeSheet` restores this on unmount, but a close initiated out
    // here races the iframe's teardown — and if the member closes Ash from the
    // compose sheet, the reopened panel would come back reading "Send a message",
    // with a back arrow, over a fresh chat.
    setPanelHeader(null)
  }, [])

  // Clearing the id here is what keeps a reopened Sidekick from stamping events
  // with the closed session's id: this provider outlives the iframe, so without
  // the reset the ref holds the old id until the new ASH_SESSION_STARTED arrives
  // — and if the member reopens and types nothing, forever. Safe against the
  // `ashClosed` that reads it, because the poptart tracks *then* closes.
  //
  // The header is cleared by `hideSidekick`, for its own reasons — see it.
  const closeSidekick = useCallback(() => {
    hideSidekick()
    ashSessionIdRef.current = ''
  }, [hideSidekick])

  // This is the URL where we can find the Virtual Assistant. We load it into
  // an iframe in the member app.
  const sourceUrl = process.env.VIRTUAL_ASSISTANT_SRC_URL

  // A `VIRTUAL_ASSISTANT_SRC_URL` that won't parse (`ash.sidecarhealth.com`, no
  // scheme) turns the origin check below off and reverts this environment to
  // trusting every frame on the page. `isFromAssistant` can't say so itself —
  // it never sees which env var supplied the value — so the report belongs here,
  // where the variable can be named. Once per mount, not per message: a bad
  // deploy would otherwise log on every postMessage Ash sends.
  //
  // Absent is deliberately not reported. That's the separate, already-diagnosed
  // case where Ash can't load at all, and every environment that simply doesn't
  // run Ash would log it.
  useEffect(() => {
    if (!isAssistantUrlMalformed(sourceUrl)) return

    logError('VIRTUAL_ASSISTANT_SRC_URL is not a parseable URL', {
      context: 'sidekick-origin-check',
      // The value, not just the fact — whoever reads this needs to see the typo.
      // Not a secret: it ships in the bundle as the iframe's `src`.
      sourceUrl,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sourceUrl` is a build-time constant (Vite `define`); listing it would only risk re-reporting.
  }, [])

  // Helper function to get the virtual assistant iframe element. We use a function
  // instead of setting to a const because it may not be rendered when this hook
  // is instantiated.
  const getSidekickIFrame = () => {
    const vaElement = document.getElementById('shSidekick')

    // If the virtual assistant iframe isn't found, we can't send it a message.
    // This is a warning because it's not a big deal if it's not found yet.
    // It's just a sign that the iframe isn't rendered yet.
    if (!vaElement) {
      console.warn(
        'Virtual assistant iframe not found. It may not be rendered yet.',
      )
      return undefined
    }

    return vaElement
  }

  /**
   * The setConfig function lets us pass a message to the virtual assistant that
   * helps it set up configs to initialize/work correctly. The configs are
   * cumulative so you can just send the one you want and it'll either get
   * merged in or updated if it already exists.
   *
   * @param {object} config - The configuration data to send to the assistant
   */
  const setConfig = useCallback(config => {
    const vaElement = getSidekickIFrame()

    // If we don't have the iFrame, bail
    if (!vaElement) return

    vaElement.contentWindow.postMessage({ type: 'SET_CONFIG', config }, '*')
  }, [])

  /**
   * ASH-887: tell the Ash iframe live chat couldn't be opened, so its
   * MemberCareContact card can fall back to the phone number instead of leaving
   * a dead "Chat with Member Care" button.
   *
   * @param {string} [sessionId] - Ash session id to echo back.
   */
  const postMemberCareChatFailed = useCallback(sessionId => {
    const vaElement = getSidekickIFrame()
    if (!vaElement) return

    vaElement.contentWindow.postMessage(
      { type: 'MEMBER_CARE_CHAT_FAILED', sessionId },
      '*',
    )
  }, [])

  /**
   * Tell Ash the member pressed the back arrow in our poptart header.
   *
   * Embedded, that arrow is the only one on screen — Ash draws no header row of
   * its own when it has a host (`onepoptart.pdf`) — but the step it goes back
   * to is Ash's, not ours. So we report the press and Ash decides what back
   * means; this app never needs to know the compose sheet has steps at all.
   *
   * No payload, deliberately: an id or a step name here would let this app
   * steer a flow it doesn't model. It's exposed on the context so
   * `SidekickPoptart` can hand it to the poptart's `onBack`, and it's only ever
   * _shown_ when Ash has asked for it via `panelHeader.canGoBack`, so a stray
   * call can't reach an Ash that isn't listening.
   */
  const postPanelBackPressed = useCallback(() => {
    const vaElement = getSidekickIFrame()
    if (!vaElement) return

    vaElement.contentWindow.postMessage({ type: 'PANEL_BACK_PRESSED' }, '*')
  }, [])

  /**
   * Re-fetch the inbox after Ash files a ticket, once Jira has caught up.
   *
   * **Why it waits.** Not politeness — fetching immediately can break the list.
   * The comm service returns a just-created ticket before its comments exist
   * (the backend adds one and Jira automation adds another), and both the
   * reducer and the row read comments unconditionally:
   * `FETCH_MEMBER_COMMS_MESSAGES_SUCCESS` sorts on the first comment's
   * `created`, and `RequestRow` reads the last comment's `updated`. A
   * comment-less ticket would throw inside the reducer. `RequestForm` hit the
   * same wall and waits the same 3.2s before it fetches and routes — this is
   * that precedent, reached from the other direction (Ash navigates first, then
   * the list catches up, because the member is already looking at it).
   *
   * Both fetches: the list, and the unresolved counter behind the "Messages"
   * badge. They come from different endpoints, and a count that disagrees with
   * the rows under it is its own bug report.
   *
   * **`awaitNewTicket()` first, and it's what makes the wait presentable.** It
   * marks the list as knowably behind reality, which does two things the delay
   * alone could not. The inbox shows its loading state for those seconds rather
   * than whatever it has — a stale list, or for a member who had never opened
   * Messages, _nothing_. And it stops `RequestList` fetching on mount: that
   * fetch would win the race, return a list from before the ticket, and render
   * it as settled content that this one then visibly corrects. Which is exactly
   * what the member reported seeing.
   *
   * The skeleton hides rows the member already had, deliberately — that isn't
   * an oversight to fix. Keeping them was tried first and reproduced the same
   * complaint: they're missing the message just sent, so this fetch swaps them
   * out and the swap reads as the page refreshing underneath. Showing nothing
   * settled is the point.
   *
   * Dispatched synchronously, before the navigate, so the flag is in the store
   * by the time the inbox mounts and reads it.
   */
  const inboxRefreshRef = useRef(null)
  const scheduleInboxRefresh = useCallback(() => {
    clearTimeout(inboxRefreshRef.current)
    dispatchRef.current(awaitNewTicket())

    inboxRefreshRef.current = setTimeout(() => {
      dispatchRef.current(fetchMemberCommsMessages())
      dispatchRef.current(fetchUnresolvedMessages())
    }, INBOX_INDEXING_DELAY_MS)
  }, [])

  // Don't dispatch into a store this provider no longer belongs to: it outlives the
  // poptart but not a logout, and the timer is longer than the gap between clicking
  // "View your message" and a session ending.
  //
  // This drops the pending fetch and leaves `awaitingNewTicket` set, which is fine
  // rather than lucky: the only thing that unmounts this provider is the app going
  // away, and the two logout actions reset `MemberCommunication` to `initialState`
  // wholesale (`store/reducers.js`) — flag included.
  useEffect(() => () => clearTimeout(inboxRefreshRef.current), [])

  /**
   * Reset the chat ready state. This should be called when navigating away from
   * pages that contain the virtual assistant iframe, since each iframe instance
   * needs to send CHAT_READY when it loads.
   */
  const resetChatReady = useCallback(() => {
    setIsChatReady(false)
  }, [])

  /**
   * This effect starts listeners for any messages that are broadcast from the
   * assistant so that we can react to them. They will be pushed to us via
   * parent.postMessage.
   */
  useEffect(() => {
    // If we already have a listener, don't set up another one
    if (listenerRef.current) return

    const vaListener = event => {
      // Only listen to Ash. `window.addEventListener('message')` hears every
      // frame on the page, and member pages embed frames we don't control —
      // Contentful videos (`utils/contentful/index.jsx`), help videos, Stripe.
      // Any of them can `window.parent.postMessage` whatever they like.
      //
      // This mattered less when the payload only fed Heap. It opens live chat
      // now, and writes a Genesys custom attribute a Member Care agent reads,
      // so an unchecked sender can reach a human being.
      if (!isFromAssistant(event.origin, sourceUrl)) return

      const data = event.data

      // Guard clause: if no data is provided, exit early
      if (!data) return

      // Additional guard: only process messages that look like they're from our VA
      if (!data.type || typeof data.type !== 'string') return

      // The assistant will send us a message when it's ready to
      // receive messages. Without this, we might try to send messages
      // before it has its listeners set up.
      if (data.type === 'CHAT_READY') {
        setIsChatReady(true)
      }

      /**
       * The member sent their first message, so an Ash session now exists.
       *
       * Recorded for this app's _own_ Ash events (ashClosed / ashExpandToggled
       * / ashMaximizeToggled / memberCareChatOpenedFromAsh), which fire out
       * here and would otherwise have no way to name the conversation they
       * describe. The iframe's own TRACK_EVENTs already carry `sessionId`
       * themselves.
       */
      if (data.type === 'ASH_SESSION_STARTED') {
        if (typeof data.sessionId === 'string') {
          ashSessionIdRef.current = data.sessionId
        }
      }

      /**
       * Handles tracking events from the Virtual Assistant iframe.
       *
       * Receives tracking events via postMessage and forwards them to Heap
       * analytics. Metadata is flattened to ensure Heap can access all
       * properties directly.
       */
      if (data.type === 'TRACK_EVENT') {
        // Guard: ensure Heap is available before tracking
        if (!window.heap) {
          console.warn('Heap analytics not available for TRACK_EVENT')
          return
        }

        // Extract event name and forward all other data to Heap
        const { type, event, ...eventData } = data

        window.heap.track(event, eventData)
      }

      /**
       * Ash is showing something that isn't the chat — today, the "Send a
       * message" compose sheet — and is handing us the header for it.
       *
       * Embedded, Ash renders no header row of its own at all: ours is the only
       * one, so everything that was in its row has to arrive here. The title,
       * and `canGoBack`, which drives both the back arrow and (inverted)
       * whether we keep offering to collapse — see `SidekickPoptart`, which
       * reads this. Two stacked headers, each with its own ✕, is what design
       * kept objecting to (2026-08-20, then `onepoptart.pdf`).
       *
       * **An absent `title` means restore, not clear.** Ash can't send our
       * default back because it doesn't know it, so omitting the key is how it
       * says "yours again" — and `canGoBack` goes with it, because our own
       * header has no step of Ash's behind it. Anything non-string is ignored
       * rather than rendered: `Poptart` would happily put an object in its
       * `<Title>`.
       */
      if (data.type === 'SET_PANEL_HEADER') {
        const title =
          typeof data.title === 'string' && data.title ? data.title : null

        setPanelHeader(
          title ? { title, canGoBack: data.canGoBack === true } : null,
        )
      }

      /**
       * Handles opening the Genesys Member Care chat from the Virtual
       * Assistant. This allows Ash to seamlessly hand off to a live agent when
       * needed.
       */
      if (data.type === 'OPEN_MEMBER_CARE_CHAT') {
        // Close Sidekick on mobile (< 640px) to make room for Genesys
        const isMobile = window.matchMedia('(max-width: 639px)').matches
        if (isMobile) {
          // Not `closeSidekick`: the session id below is read from the ref when
          // Ash didn't send one, and `closeSidekick` clears it. `hideSidekick`
          // takes the panel down and leaves the session alone.
          hideSidekick()
        }

        // The hand-off's own sessionId when Ash sent one, else the session we
        // were told about. Both should agree; preferring the message means a
        // hand-off from a surface that predates ASH_SESSION_STARTED still lands
        // with the right id rather than the ref's.
        const resolvedSessionId =
          typeof data.sessionId === 'string' && data.sessionId
            ? data.sessionId
            : ashSessionIdRef.current

        if (window.heap) {
          window.heap.track(
            'memberCareChatOpenedFromAsh',
            withAshSession(resolvedSessionId),
          )
        }
        // Hand the session id over before asking for the Messenger, so it's in
        // the Database plugin by the time the conversation starts rather than
        // racing an interaction the agent may already have been handed.
        //
        // This orders the calls, not the writes. `addChatAttributes` writes
        // synchronously only once the Database plugin has announced itself; on a
        // cold start it subscribes to `Database.ready` and the update lands after
        // the open regardless of the order here — and `openChatWithRecovery`
        // (ASH-887) can retry the open several times over seconds, widening that
        // window. Nothing in this app can close it; see the note in
        // `addChatAttributes`. Writing first is simply the earliest we can.
        //
        // `addChatAttributes`, not `setChatAttributes` — the latter replaces
        // `customAttrs` wholesale and would drop the member's name, uuid, and the
        // `skill` that routes them to the right queue. See its own note.
        //
        // Only what Ash actually sent. Writing a blank `ashSessionId` would give
        // an agent an attribute they can't look anything up with, which reads as
        // context we captured and lost. Same absent-not-empty rule as
        // `withAshSession` above.
        if (resolvedSessionId) {
          addChatAttributes({ ashSessionId: resolvedSessionId })
        }

        openChatWithRecovery({
          sessionId: resolvedSessionId,
          onPermanentFailure: postMemberCareChatFailed,
        })
      }

      /**
       * "View your message" on Ash's post-submit confirmation: show the member
       * the ticket they just filed, in the inbox that holds it.
       *
       * Both halves are things Ash cannot do from inside its iframe —
       * navigating `window.location` there would load the inbox _into the
       * poptart_ — so it asks us. `/inbox` is this app's own route, so a
       * client-side navigate is right; `?splash=false` is not needed here,
       * because we never left the SPA.
       *
       * Navigate first, then close. Closing first would unmount the poptart and
       * hand the member a blank screen for the length of the route transition;
       * this way the inbox is already rendering behind Ash as it goes.
       *
       * Then re-fetch, because a client-side navigate keeps the store: nothing
       * on the way into `/inbox` will fetch again once the list is populated
       * (`RequestList` and `Inbox/Home` both guard on "don't have it yet"), so
       * a member who opened Messages earlier in the session would arrive at the
       * list as it was _before_ they filed — no new ticket. See
       * `scheduleInboxRefresh` for why it waits.
       */
      if (data.type === 'OPEN_MEMBER_INBOX') {
        const resolvedSessionId =
          typeof data.sessionId === 'string' && data.sessionId
            ? data.sessionId
            : ashSessionIdRef.current

        if (window.heap) {
          window.heap.track(
            'memberInboxOpenedFromAsh',
            withAshSession(resolvedSessionId),
          )
        }

        navigateRef.current('/inbox')
        scheduleInboxRefresh()
        // Not `setIsOpen(false)`: the session id has to be cleared with it, or a
        // reopened Ash stamps the next conversation's events with this one's. That's
        // what `closeSidekick` is for — see its own note.
        closeSidekick()
      }
    }

    window.addEventListener('message', vaListener)
    listenerRef.current = vaListener

    // Cleanup function prevents memory leaks by:
    // 1. Removing the event listener from window
    // 2. Clearing the ref to allow garbage collection
    return () => {
      if (listenerRef.current) {
        window.removeEventListener('message', listenerRef.current)
        listenerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sourceUrl` is omitted deliberately: it's a build-time constant (Vite `define`), so listing it would only risk re-registering the listener. The ref guard at the top of this effect makes a re-run a no-op anyway.
  }, [postMemberCareChatFailed, closeSidekick, hideSidekick])

  /**
   * Subscribe to Genesys Messenger open/close state via useChatWidget. This
   * allows us to reposition Sidekick when Genesys is open to avoid overlap.
   */
  useEffect(() => {
    const unsubscribe = subscribeToMessengerState(setIsGenesysChatOpen)
    return unsubscribe
  }, [])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      closeSidekick,
      // The live Ash session id, or '' before the member's first message. A
      // getter because the id arrives by postMessage into a ref — reading it at
      // click time is what keeps a session start from re-rendering every consumer
      // of this context. Analytics call sites only; pass it through
      // `withAshSession`, which owns the omit-when-empty rule.
      getAshSessionId,
      isChatReady,
      isGenesysChatOpen,
      isOpen,
      // Whether the panel is the full screen rather than the docked sheet. Set
      // from the viewport at each open — see the state itself.
      isMaximized,
      openSidekick,
      // How Ash has asked us to draw our header (`{ title, canGoBack }`), or null for
      // our own. The default title lives in `SidekickPoptart` with the rest of that
      // header's props, so this stays null rather than carrying a fallback nobody
      // else can see.
      panelHeader,
      // The back arrow that header shows when `canGoBack` is set. Paired with it here
      // because a consumer needs both or neither.
      postPanelBackPressed,
      resetChatReady,
      setConfig,
      setIsMaximized,
      sourceUrl,
    }),
    [
      closeSidekick,
      getAshSessionId,
      isChatReady,
      isGenesysChatOpen,
      isOpen,
      isMaximized,
      openSidekick,
      panelHeader,
      postPanelBackPressed,
      resetChatReady,
      setConfig,
      sourceUrl,
    ],
  )

  return <Sidekick.Provider value={contextValue}>{children}</Sidekick.Provider>
}
